import { IShoppingCartContext, ShippingMethodTypes } from '@monofront/types';
import { DeliveryBarError } from '../../hooks/useDeliveryBarError';
import { DeliveryBarMachineContextShippingMethod } from '../../stateMachines/deliveryBar/types';
import { ErrorTypes } from '../DeliveryBarContext';
import { getIsFresh, getIsUltraFresh } from '../../utils/product';

type Validator<P> = (params: P) => DeliveryBarValidationResult;

export type DeliveryBarValidationParams = {
  selectedShippingMethod: DeliveryBarMachineContextShippingMethod | null;
  shoppingCart: IShoppingCartContext['shoppingCart'];
  isShippingFreshMethodEligible: boolean;
  isShippingUltraFreshMethodEligible: boolean;
  waitingProduct?: { tags: string[] } | null;
};

export type DeliveryBarValidationResultValid = { isValid: true };

export type DeliveryBarValidationResultInvalid = {
  isValid: false;
  error: Omit<NonNullable<DeliveryBarError>, 'errorAtState'>;
};

export type DeliveryBarValidationResult =
  | DeliveryBarValidationResultValid
  | DeliveryBarValidationResultInvalid;

const validateShoppingCartEligibility: Validator<{
  selectedShippingMethod: DeliveryBarMachineContextShippingMethod | null;
  shoppingCart: DeliveryBarValidationParams['shoppingCart'];
  isShippingFreshMethodEligible: boolean;
  isShippingUltraFreshMethodEligible: boolean;
}> = ({
  selectedShippingMethod,
  shoppingCart,
  isShippingFreshMethodEligible,
  isShippingUltraFreshMethodEligible,
}) => {
  if (!shoppingCart || shoppingCart.products?.length === 0) {
    return { isValid: true };
  }

  if (
    ((shoppingCart.hasFreshProduct || shoppingCart.hasUltraFreshProduct) &&
      selectedShippingMethod?.type === ShippingMethodTypes.PICKUP) ||
    (shoppingCart.hasFreshProduct && !isShippingFreshMethodEligible) ||
    (shoppingCart.hasUltraFreshProduct && !isShippingUltraFreshMethodEligible)
  ) {
    return {
      isValid: false,
      error: {
        errorType: ErrorTypes.INELIGIBLE_PRODUCTS_IN_CART,
      },
    };
  }

  return { isValid: true };
};

const validateWaitingProductEligibility: Validator<DeliveryBarValidationParams> =
  ({ waitingProduct, isShippingFreshMethodEligible, isShippingUltraFreshMethodEligible }) => {
    if (!waitingProduct) {
      return { isValid: true };
    }

    if (
      (getIsFresh(waitingProduct.tags) && !isShippingFreshMethodEligible) ||
      (getIsUltraFresh(waitingProduct.tags) && !isShippingUltraFreshMethodEligible)
    ) {
      return {
        isValid: false,
        error: { errorType: ErrorTypes.INELIGIBLE_PRODUCT_ADDED_TO_CART },
      };
    }

    return { isValid: true };
  };

const VALIDATORS: Validator<DeliveryBarValidationParams>[] = [
  validateShoppingCartEligibility,
  validateWaitingProductEligibility,
];

export const validateDeliveryBarState = (
  params: DeliveryBarValidationParams,
): DeliveryBarValidationResult => {
  for (const validate of VALIDATORS) {
    const result = validate(params);

    if (!result.isValid) {
      return result;
    }
  }

  return { isValid: true };
};
